// src/components/wellfinder/WellLayer.jsx
//
// Render-nothing map controller for AIPrime WellFinder product.
// Mirrors the SiteLayer pattern exactly.
//
// Responsibilities:
//   - Force RRC well layers visible on mount, restore on unmount
//   - Register detected-well and offset-line sources/layers
//   - Build offset lines GeoJSON from matched pair data
//   - Own click handlers for both rrc-well-points and detected-well-points
//   - Sync filter state to map layer filters
//   - Highlight selected well pair
//   - Filter detected wells by active operating AOI
//
// Props:
//   map  — MapLibre Map instance (from MapView via MapPage)

import { useEffect, useRef, useCallback } from "react";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import { useWellFinderStore } from "../../stores/useWellFinderStore";
import { useLayersStore } from "../../stores/useLayersStore";
import { useWellSelectionStore } from "../../stores/useWellSelectionStore";
import { useOperatingAoiContext } from "../../stores/useOperatingAoiContext";
import { useLayerSetsStore } from "../../stores/useLayerSetsStore";
import { focusWell, safeMapOp } from "./cameraFocus";
import { WELLFINDER_LAYERS } from "../../constants/mapLayerIds";
import {
  detectedCoreColor,
  detectedCoreStrokeColor,
  detectedCoreStrokeWidth,
  rejectedCirclePaint,
  IS_REJECTED,
  IS_NOT_REJECTED,
} from "./wellMapEncoding";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DETECTED_SOURCE = "detected-wells";
const OFFSET_SOURCE   = "well-offset-lines";

// Matched-well offset ceiling. Stored truth: max offset_m for
// correlation_type='well_match' is 49.80m (CANDIDATE_M=50.0). Anything longer
// is fabricated client-side — drop it, never render.
const MAX_OFFSET_M = 60;

// Layer ids live in constants/mapLayerIds.js so MapView can build its
// click-priority list from the same source. Aliased back to LAYER here so
// every call site below is unchanged.
const LAYER = WELLFINDER_LAYERS;

// Match status → color
const STATUS_COLORS = {
  strong:      "#22c55e",   // green
  candidate:   "#f59e0b",   // amber
  unverified:  "#ef4444",   // red
  qc_conflict: "#f97316",   // orange
};

// The adjudication palette and every expression derived from it now live in
// wellMapEncoding.js, which the on-map legend reads from the same object. See
// that file for why the fill is the verdict and why pending is recessive
// rather than hidden.

// Offset line color expression (driven by match_confidence in props)
//
// 'unverified' has an explicit case. Without one it fell through to the grey
// fallback — the same grey as "a confidence value this client does not
// recognise" — so a match whose vouching an analyst had withdrawn rendered
// identically to one the client failed to parse. Reopen now downgrades a
// correlation to 'unverified' instead of deleting it, so that case is reachable
// and has to say something.
//
// Red is not a new colour here: STATUS_COLORS.unverified is already red on the
// point layer and MatchStatusBadge already renders "Unverified" in red. This
// makes the offset line agree with them rather than invent a fifth language.
const OFFSET_COLOR = [
  "match", ["get", "match_confidence"],
  "strong",      STATUS_COLORS.strong,
  "candidate",   STATUS_COLORS.candidate,
  "unverified",  STATUS_COLORS.unverified,
  "qc_conflict", STATUS_COLORS.qc_conflict,
  "#94a3b8",   // fallback gray — an UNRECOGNISED value, not a known-weak one
];

// ── Non-hue channels ────────────────────────────────────────────────────────
//
// Hue alone is not sufficient. Green/red on thin dashed lines is the classic
// colour-deficiency pair, and before this only 'unverified' had a second
// channel — every other confidence was distinguished by colour and nothing
// else. Each of the four now has a distinct (width, opacity) pair, so the four
// states are separable in greyscale:
//
//   qc_conflict  3.0 / 0.90   widest    — genuine registry disagreement. LOUD,
//                                         because it is an alert, not a weak
//                                         match: it is not "less than
//                                         candidate", it is a different axis.
//   strong       2.2 / 0.85   thick     — vouched for
//   candidate    1.5 / 0.70   baseline  — unchanged from the previous flat
//                                         values, so the 1,381 legacy candidate
//                                         lines look exactly as they did
//   unverified   0.9 / 0.40   thinnest  — withdrawn; recedes into background
//
// Width is the primary discriminator because four distinct strokes read at a
// glance; opacity reinforces the same ordering. The one appearance change to
// legacy data is that 'strong' lines get thicker (1.5 -> 2.2) — unavoidable if
// strong and candidate are to differ by anything but colour.
//
// line-dasharray is NOT used, and an earlier comment here claiming it "cannot"
// carry the signal was wrong: in the installed spec (maplibre-gl 5.15.0) it is
// cross-faded-data-driven, so a per-feature dash pattern is possible. It is
// left alone because cross-faded properties interpolate between patterns during
// zoom and that behaviour needs a browser to verify. Width and opacity are
// plain data-driven and need no such caveat. Dash spacing remains the obvious
// third channel if a fourth is ever wanted.
const OFFSET_WIDTH = [
  "match", ["get", "match_confidence"],
  "qc_conflict", 3.0,
  "strong",      2.2,
  "candidate",   1.5,
  "unverified",  0.9,
  1.5,   // unrecognised value — baseline, neither emphasised nor hidden
];

const OFFSET_OPACITY = [
  "match", ["get", "match_confidence"],
  "qc_conflict", 0.9,
  "strong",      0.85,
  "candidate",   0.7,
  "unverified",  0.4,
  0.7,
];

// Filter expressions
// IS_REJECTED / IS_NOT_REJECTED are imported from wellMapEncoding.js — the
// layer that draws a state and the legend that names it read one definition.

// Base vtile URL
// THE LAYER KEY IS THE ASSET TYPE, and it changed with the plane split.
//
// /vtiles/<asset_type>/… is the catalog family: the registry admits any key that
// is is_active in aiprime.asset_types, and api.vector_tile selects from
// aiprime.vector_assets_visible WHERE asset_type = <key>. Detections used to be
// vector_assets_org rows typed 'detected_well'; 062 moved them to
// ai_observations, where the view's third arm exposes them as their class_key —
// 'well_head'.
//
// 'detected_well' IS STILL is_active, which is why this failed silently rather
// than 404ing: migration 065 kept that one key alive precisely because this line
// hardcodes it, reasoning that deactivating it "would turn a silent empty
// response into a console error on a shipped surface". The silent empty response
// is what shipped. Measured at z12/878/1657 over the corpus:
//
//   detected_well   0 bytes
//   well_head       37,199 bytes
//
// The MVT layer inside the tile is named "detections" — ST_AsMVT names it, not
// the asset type — and every layer on this source declares that source-layer.
const VTILE_BASE = `${window.location.origin}/vtiles/wellfinder_detections/{z}/{x}/{y}.mvt`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

// Haversine length in meters between two [lng, lat] points.
function offsetMeters(aLng, aLat, bLng, bLat) {
  const R = 6371000;
  const toRad = Math.PI / 180;
  const dLat = (bLat - aLat) * toRad;
  const dLng = (bLng - aLng) * toRad;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(aLat * toRad) * Math.cos(bLat * toRad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function buildOffsetLines(pairs) {
  return {
    type: "FeatureCollection",
    features: pairs
      .filter(p => p.rrcLng != null && p.rrcLat != null)
      .filter(p => {
        // Sanity guard: RRC endpoints are all within CANDIDATE_M (50m).
        // A longer line is a client-side fabrication — drop and warn.
        const meters = offsetMeters(p.detectedLng, p.detectedLat, p.rrcLng, p.rrcLat);
        if (meters > MAX_OFFSET_M) {
          console.warn("[WellLayer] dropped offset line >60m", {
            detectionId: p.detectionId,
            meters: Math.round(meters),
          });
          return false;
        }
        return true;
      })
      .map(p => ({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [
            [p.detectedLng, p.detectedLat],
            [p.rrcLng, p.rrcLat],
          ]
        },
        properties: {
          detection_id:     p.detectionId,
          match_confidence: p.matchConfidence,
          offset_m:         p.offsetM,
        }
      }))
  };
}

function tileUrl(aoiId) {
  return aoiId
    ? `${VTILE_BASE}?aoi=${aoiId}`
    : VTILE_BASE;
}

// ---------------------------------------------------------------------------
// WellLayer
// ---------------------------------------------------------------------------

export default function WellLayer({ map }) {
  const initializedRef = useRef(false);
  const pairCacheRef   = useRef([]);

  const selectWell         = useWellFinderStore(s => s.selectWell);
  const clearSelection     = useWellFinderStore(s => s.clearSelection);
  const selectedWellId     = useWellFinderStore(s => s.selectedWellId);
  const selectedWellType   = useWellFinderStore(s => s.selectedWellType);
  const activeFilters      = useWellFinderStore(s => s.activeFilters);
  const setOffsetLines     = useWellFinderStore(s => s.setOffsetLines);
  const setStats           = useWellFinderStore(s => s.setStats);
  const savePriorRrcState  = useWellFinderStore(s => s.savePriorRrcState);
  const clearPriorRrcState = useWellFinderStore(s => s.clearPriorRrcState);
  const selectRrcOnly      = useWellFinderStore(s => s.selectRrcOnly);

  const setAssetGeometry = useLayersStore(s => s.setAssetGeometry);

  // The project bootstrap's restore barrier. See the RRC claim effect below.
  const layerSetsRestored = useLayerSetsStore(s => s.layerSetsRestored);
  const clearLegacyWell = useWellSelectionStore(s => s.clearWell);

  const activeOperatingAoiId = useOperatingAoiContext(s => s.activeOperatingAoiId);

  // ---------------------------------------------------------------------------
  // Fetch detail for a clicked well and update store
  // ---------------------------------------------------------------------------
  const fetchAndSelect = useCallback(async (id, type) => {
    useWellFinderStore.getState().setLoadingDetail(true);
    useWellFinderStore.getState().setDetailError(null);

    try {
      const res = await fetchWithAuth(`/api/v1/wells/${id}/detail`);
      if (!res.ok) {
        if (res.status === 404 && type === "rrc") {
          selectWell(id, type, null);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const detail = await res.json();
      selectWell(id, type, detail);
    } catch (err) {
      console.error("[WellLayer] detail fetch failed", err);
      useWellFinderStore.getState().setDetailError(err.message);
      selectWell(id, type, null);
    } finally {
      useWellFinderStore.getState().setLoadingDetail(false);
    }
  }, [selectWell]);

  // ---------------------------------------------------------------------------
  // Fetch offset pairs + build lines
  //
  // RRC endpoint coords (rrc_lng/rrc_lat) now ride along on the wells/near
  // payload — the server-side join already exists. The old client-side
  // rrc-coords re-join was deleted: it index-keyed RRC coords by position in
  // an unfiltered `wells` array against a filtered `pairs` array, so indices
  // diverged at the first unmatched well and every subsequent pair got another
  // detection's RRC endpoint (kilometer-long lines at low zoom). Building pairs
  // straight from the payload removes that bug class, the second round-trip,
  // and the silent 200-well cap.
  // ---------------------------------------------------------------------------
  const fetchOffsetPairs = useCallback(async (map) => {
    const bounds = map.getBounds();
    const bbox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ].join(",");

    const aoiParam = activeOperatingAoiId ? `&aoi_id=${activeOperatingAoiId}` : "";

    try {
      const res = await fetchWithAuth(
        `/api/v1/wells/near?bbox=${bbox}&limit=500${aoiParam}`
      );
      if (!res.ok) return;

      const data  = await res.json();
      const wells = data.wells || [];

      // Update stats — exclude no_well from counts
      const stats = { strong: 0, candidate: 0, unverified: 0, total: 0 };
      wells.forEach(w => {
        if (w.qc_type === "no_well") return;
        stats.total++;
        if (stats[w.match_status] !== undefined) stats[w.match_status]++;
      });
      setStats(stats);

      // Cache all wells for RRC reverse-lookup
      pairCacheRef.current = wells.map(w => ({
        detectionId: w.detection_id,
        matchedApi:  w.matched_api ?? null,
        rrcId:       w.rrc_id ?? null,
      }));

      // Build pairs directly from the payload — RRC coords are already present.
      const pairs = wells
        .filter(w => w.matched_api && w.match_confidence && w.rrc_lng != null && w.rrc_lat != null)
        .map(w => ({
          detectionId:     w.detection_id,
          matchedApi:      w.matched_api,
          rrcId:           w.rrc_id ?? null,
          detectedLng:     w.lng,
          detectedLat:     w.lat,
          rrcLng:          w.rrc_lng,
          rrcLat:          w.rrc_lat,
          matchConfidence: w.match_confidence,
          offsetM:         w.offset_m,
        }));

      const fc = buildOffsetLines(pairs);
      setOffsetLines(fc);

      safeMapOp(map, m => {
        const src = m.getSource(OFFSET_SOURCE);
        if (src) src.setData(fc);
      });

    } catch (err) {
      console.warn("[WellLayer] wells/near fetch failed", err);
    }
  }, [setOffsetLines, setStats, activeOperatingAoiId]);

  // ---------------------------------------------------------------------------
  // Mount: force RRC wells visible, register sources + layers
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || initializedRef.current) return;

    // 1) The RRC surface-well claim used to sit here. It now has its own effect
    //    below, gated on the layer-set restore barrier — see the comment there.
    clearLegacyWell();

    // 2) Detected wells vector tile source — include AOI if already set
    if (!map.getSource(DETECTED_SOURCE)) {
      map.addSource(DETECTED_SOURCE, {
        type:    "vector",
        tiles:   [tileUrl(activeOperatingAoiId)],
        minzoom: 11,
        maxzoom: 18,
      });
    }

    // 3) Offset lines GeoJSON source
    if (!map.getSource(OFFSET_SOURCE)) {
      map.addSource(OFFSET_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    // 4) Offset line layer
    if (!map.getLayer(LAYER.OFFSET_LINE)) {
      map.addLayer({
        id:      LAYER.OFFSET_LINE,
        type:    "line",
        source:  OFFSET_SOURCE,
        minzoom: 11,
        paint: {
          "line-color":     OFFSET_COLOR,
          // Width and opacity are both data-driven so every confidence is
          // separable without relying on hue. See OFFSET_WIDTH.
          "line-width":     OFFSET_WIDTH,
          "line-opacity":   OFFSET_OPACITY,
          "line-dasharray": [3, 2],
        }
      }, "anchor-assets");
    }

    // 5) Selected offset line highlight
    if (!map.getLayer(LAYER.OFFSET_SELECTED)) {
      map.addLayer({
        id:      LAYER.OFFSET_SELECTED,
        type:    "line",
        source:  OFFSET_SOURCE,
        minzoom: 11,
        filter:  ["==", ["get", "detection_id"], "__none__"],
        paint: {
          "line-color":   "#ffffff",
          "line-width":   2.5,
          "line-opacity": 0.9,
        }
      }, "anchor-assets");
    }

    // 6) Detected well halo (selection highlight)
    if (!map.getLayer(LAYER.DETECTED_HALO)) {
      map.addLayer({
        id:             LAYER.DETECTED_HALO,
        type:           "circle",
        source:         DETECTED_SOURCE,
        "source-layer": "detections",
        minzoom:        11,
        filter:         ["==", ["get", "id"], "__none__"],
        paint: {
          "circle-radius":       14,
          "circle-color":        "#ffffff",
          "circle-opacity":      0.15,
          "circle-stroke-width": 0,
        }
      }, "anchor-assets");
    }

    // 6b) The RRC well halo used to be created here. It now has its own effect
    //     below, because it is the one layer WellFinder puts on a source it
    //     does not own — see the comment there.

    // 7) no_well halo (selection highlight for QC-cleared wells)
    if (!map.getLayer(LAYER.DETECTED_NO_WELL_HALO)) {
      map.addLayer({
        id:             LAYER.DETECTED_NO_WELL_HALO,
        type:           "circle",
        source:         DETECTED_SOURCE,
        "source-layer": "detections",
        minzoom:        11,
        filter:         ["==", ["get", "id"], "__none__"],
        paint: {
          "circle-radius":       14,
          "circle-color":        "#ffffff",
          "circle-opacity":      0.15,
          "circle-stroke-width": 0,
        }
      }, "anchor-assets");
    }

    // 8) no_well layer — black fill, red stroke, distinct from match-status wells
    if (!map.getLayer(LAYER.DETECTED_NO_WELL)) {
      map.addLayer({
        id:             LAYER.DETECTED_NO_WELL,
        type:           "circle",
        source:         DETECTED_SOURCE,
        "source-layer": "detections",
        minzoom:        11,
        filter:         IS_REJECTED,
        paint: {
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            9, 4,
            12, 6,
          ],
          ...rejectedCirclePaint(),
        }
      }, "anchor-labels");
    }

    // 9) Detected well core — excludes no_well
    if (!map.getLayer(LAYER.DETECTED_CORE)) {
      map.addLayer({
        id:             LAYER.DETECTED_CORE,
        type:           "circle",
        source:         DETECTED_SOURCE,
        "source-layer": "detections",
        minzoom:        11,
        filter:         IS_NOT_REJECTED,
        paint: {
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            9, 4,
            12, 6,
          ],
          // Derived from wellMapEncoding.js, which the legend reads too. The
          // white ring on pending is a STROKE rather than a second circle
          // layer: DETECTED_HALO is the SELECTION highlight, driven by
          // setFilter on click, and a second meaning on it would make a
          // selected well and an unreviewed one look alike.
          "circle-color":          detectedCoreColor(),
          "circle-opacity":        0.9,
          "circle-stroke-color":   detectedCoreStrokeColor(),
          "circle-stroke-width":   detectedCoreStrokeWidth(),
          "circle-stroke-opacity": 0.8,
        }
      }, "anchor-labels");
    }

    // 10) Detected well outer ring — excludes no_well
    if (!map.getLayer(LAYER.DETECTED_RING)) {
      map.addLayer({
        id:             LAYER.DETECTED_RING,
        type:           "circle",
        source:         DETECTED_SOURCE,
        "source-layer": "detections",
        minzoom:        11,
        filter:         IS_NOT_REJECTED,
        paint: {
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            11, 9,
            15, 13,
          ],
          "circle-color":          "rgba(0,0,0,0)",
          "circle-stroke-color": [
            "match",
            ["coalesce", ["get", "match_status"], "unverified"],
            "strong",      STATUS_COLORS.strong,
            "candidate",   STATUS_COLORS.candidate,
            "unverified",  STATUS_COLORS.unverified,
            "qc_conflict", STATUS_COLORS.qc_conflict,
            STATUS_COLORS.unverified,
          ],
          "circle-stroke-width":   1.5,
          "circle-stroke-opacity": 0.6,
        }
      }, "anchor-labels");
    }

    initializedRef.current = true;

    // ---------------------------------------------------------------------------
    // Cleanup on unmount
    // ---------------------------------------------------------------------------
    return () => {
      const safeMap = map;
      if (!safeMap || !safeMap.getStyle?.()) {
        initializedRef.current = false;
        return;
      }

      Object.values(LAYER).forEach(id => {
        try {
          if (safeMap.getLayer(id)) safeMap.setLayoutProperty(id, "visibility", "none");
        } catch (e) { /* map destroyed */ }
      });

      // The RRC restore used to sit here, paired with a claim in this effect's
      // body. Both moved to the claim effect below so they cannot come apart:
      // a restore here would have fired even on the paths where the claim never
      // ran, putting the wells back to a state nobody saved.
      clearSelection();
      initializedRef.current = false;
    };
  }, [map]);

  // ---------------------------------------------------------------------------
  // RRC SURFACE-WELL CLAIM
  //
  // WellFinder correlates detections against the RRC surface wells, so it turns
  // that layer on for the duration of the product and puts it back on the way
  // out. Claim and restore are one effect: they are two halves of one borrow,
  // and splitting them across two effects is how the first version got a
  // restore that could run without a claim.
  //
  // WHY THIS IS NOT IN THE MOUNT EFFECT ANY MORE — the reported bug. The mount
  // effect runs on `[map]`, which on a cold Explore → WellFinder navigation is
  // ready long before MapPage's project bootstrap has finished. The bootstrap
  // ends by applying the default layer set, and applySnapshot reconciles EVERY
  // asset geometry back to the saved value — so the claim was made and then
  // silently undone, and the surface wells did not appear. Returning to Explore
  // and coming back worked because the bootstrap was over by then. "Works the
  // second time" was the shape of the report and the shape of the race.
  //
  // layerSetsRestored is that bootstrap's barrier. Waiting on it states the
  // ordering instead of winning it: the claim lands after the last wholesale
  // write, not merely after a delay long enough that it usually does.
  //
  // THE STORE, NOT setLayoutProperty. MapView owns RRC visibility and its
  // effect would overwrite a layout property written from here on its next run
  // — which is exactly what an earlier attempt at this did. Driving
  // assets.wells.geometry.point is the whole mechanism, and it is the same
  // mechanism the RRC layer panel's own toggle uses (OpLayerRow →
  // toggleAssetGeometry), so both doors leave the layer in one state.
  //
  // SURFACE WELLS ONLY. `line` is the lateral geometry — a different layer, and
  // not what the offset lines correlate against. Left however the reviewer had
  // it.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !layerSetsRestored) return;

    // READ THROUGH getState(), not through a subscribed value. The prior state
    // has to be the one true at the moment of the claim, and subscribing would
    // also put the toggle in this effect's dependencies — so the reviewer
    // turning RRC off by hand would re-run the effect, "restore" to the value
    // it had just written, and turn it straight back on.
    const priorOn = useLayersStore.getState().assets.wells.geometry.point;
    savePriorRrcState(priorOn);
    setAssetGeometry("wells", "point", true);

    return () => {
      // Only when WellFinder found them OFF: a reviewer who already had RRC on
      // keeps it. That is what "restore" means, and why this is not an
      // unconditional turn-off.
      const { priorRrcPointOn } = useWellFinderStore.getState();
      if (priorRrcPointOn === false) {
        setAssetGeometry("wells", "point", false);
      }
      clearPriorRrcState();
    };
  }, [map, layerSetsRestored, savePriorRrcState, clearPriorRrcState, setAssetGeometry]);

  // ---------------------------------------------------------------------------
  // AOI change — update tile source URL
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;
    safeMapOp(map, m => {
      const src = m.getSource(DETECTED_SOURCE);
      if (src) src.setTiles([tileUrl(activeOperatingAoiId)]);
    });
  }, [map, activeOperatingAoiId]);

  // ---------------------------------------------------------------------------
  // RRC SELECTION HALO
  //
  // The highlight under the RRC side of a matched pair. It is the ONLY layer
  // WellFinder puts on a source it does not own: a circle layer's source is
  // fixed at creation, so the detected halo cannot be retargeted across sources
  // and the RRC side needs its own layer on MapView's rrc-well-points.
  //
  // IT HAS NEVER RENDERED, for two independent reasons, and it needs both
  // fixed — either one alone leaves it invisible.
  //
  // WRONG SOURCE-LAYER. It declared "detections", which is the MVT layer name
  // in WellFinder's OWN detections tile. rrc-well-points is a different tile
  // with a different internal layer name — "assets", as MapView's own
  // registration of rrc-well-points says. A source-layer that does not exist in
  // the tile matches no features and paints nothing, silently: MapLibre does
  // not warn, because a tile legitimately need not contain every layer a style
  // asks for. Copying the neighbouring halo block is how it got there.
  //
  // ADDED TOO EARLY TO BE ADDED AT ALL. MapView publishes mapInstance from its
  // "load" handler but creates the RRC sources in a map.once("idle") that fires
  // afterwards, so on a cold entry into WellFinder the mount effect ran, found
  // no rrc-well-points source, skipped the layer under its own guard, and never
  // looked again — initializedRef makes that effect single-shot. The halo only
  // existed at all on a SECOND entry, which is the same "works the second time"
  // shape as the surface-well claim above and has the same answer: wait for the
  // thing you depend on instead of assuming it has happened.
  //
  // So this waits. idle fires repeatedly, so the listener is removed as soon as
  // the layer is up rather than re-checking for the life of the product.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map) return;

    let settled = false;

    const addHalo = () => {
      if (settled) return;
      safeMapOp(map, m => {
        if (m.getLayer(LAYER.RRC_HALO)) { settled = true; return; }
        // MapView owns this source. Nothing here creates it — if it is absent
        // the map is not ready yet, and the next idle is the retry.
        if (!m.getSource("rrc-well-points")) return;

        m.addLayer({
          id:             LAYER.RRC_HALO,
          type:           "circle",
          source:         "rrc-well-points",
          // "assets", NOT "detections". Must match the MVT layer name inside
          // the rrc_well_surface tile, which is what MapView's rrc-well-points
          // layer declares. Keyed on the RRC asset uuid (`id`) — the same
          // property MapView's click handler reads off this layer, and the same
          // value /wells/:id/detail returns as link.rrc_asset_id.
          "source-layer": "assets",
          minzoom:        11,
          filter:         ["==", ["get", "id"], "__none__"],
          paint: {
            "circle-radius":       14,
            "circle-color":        "#ffffff",
            "circle-opacity":      0.15,
            "circle-stroke-width": 0,
          }
        }, "anchor-assets");
        settled = true;
      });
      if (settled) map.off("idle", addHalo);
    };

    addHalo();
    if (!settled) map.on("idle", addHalo);

    return () => { map.off("idle", addHalo); };
  }, [map]);

  // ---------------------------------------------------------------------------
  // Re-show layers on return to WellFinder
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;
    Object.values(LAYER).forEach(id => {
      safeMapOp(map, m => {
        if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", "visible");
      });
    });
  }, [map]);

  // ---------------------------------------------------------------------------
  // Click handlers
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;

    const handleDetectedClick = async (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const detectionId = f.properties?.source_id || f.properties?.id;
      if (!detectionId) return;
      await fetchAndSelect(detectionId, "detected");
    };

    const handleNoWellClick = async (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const detectionId = f.properties?.source_id || f.properties?.id;
      if (!detectionId) return;
      await fetchAndSelect(detectionId, "detected");
    };

    const handleRrcClick = async (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const wellId     = f.properties?.id;
      const clickedApi = f.properties?.source_id;
      if (!wellId) return;

      const match = pairCacheRef.current.find(p => p.matchedApi === clickedApi);

      if (match?.detectionId) {
        // Matched RRC point: still fetch by detectionId (detail.rrc rides along),
        // but tag the selection as the RRC side so the focus effect centers on
        // the RRC record's own coords and the RRC dot gets highlighted.
        await fetchAndSelect(match.detectionId, "rrc");
      } else {
        selectRrcOnly(wellId);
      }
    };

    const handleMapClick = (e) => {
      const detected = map.queryRenderedFeatures(e.point, {
        layers: [LAYER.DETECTED_CORE, LAYER.DETECTED_NO_WELL]
      });
      const rrc = map.queryRenderedFeatures(e.point, { layers: ["rrc-well-points"] });
      if (!detected.length && !rrc.length) clearSelection();
    };

    const setPointer  = () => { map.getCanvas().style.cursor = "pointer"; };
    const resetCursor = () => { map.getCanvas().style.cursor = ""; };

    map.on("click", LAYER.DETECTED_CORE,         handleDetectedClick);
    map.on("click", LAYER.DETECTED_NO_WELL,      handleNoWellClick);
    map.on("click", "rrc-well-points",            handleRrcClick);
    map.on("click",                               handleMapClick);
    map.on("mouseenter", LAYER.DETECTED_CORE,     setPointer);
    map.on("mouseleave", LAYER.DETECTED_CORE,     resetCursor);
    map.on("mouseenter", LAYER.DETECTED_NO_WELL,  setPointer);
    map.on("mouseleave", LAYER.DETECTED_NO_WELL,  resetCursor);

    return () => {
      map.off("click", LAYER.DETECTED_CORE,         handleDetectedClick);
      map.off("click", LAYER.DETECTED_NO_WELL,      handleNoWellClick);
      map.off("click", "rrc-well-points",            handleRrcClick);
      map.off("click",                               handleMapClick);
      map.off("mouseenter", LAYER.DETECTED_CORE,     setPointer);
      map.off("mouseleave", LAYER.DETECTED_CORE,     resetCursor);
      map.off("mouseenter", LAYER.DETECTED_NO_WELL,  setPointer);
      map.off("mouseleave", LAYER.DETECTED_NO_WELL,  resetCursor);
    };
  }, [map, fetchAndSelect, clearSelection]);

  // ---------------------------------------------------------------------------
  // Selected well highlight
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;

    // Highlight the dot matching the selected side. RRC side → light the RRC
    // point (keyed on its asset uuid) and drop the detected halo; detected side
    // → light the detected point (keyed on its source_id) and drop the RRC halo.
    // The offset line stays lit for either side (it belongs to the pair, keyed
    // on detection_id, which is selectedWellId in both cases).
    const detail   = useWellFinderStore.getState().selectedWell;
    const isRrc     = selectedWellType === "rrc";
    const detectedKey = (!isRrc && selectedWellId) ? selectedWellId : "__none__";
    const rrcKey      = (isRrc && detail?.link?.rrc_asset_id) ? detail.link.rrc_asset_id : "__none__";
    const offsetKey   = selectedWellId ?? "__none__";

    safeMapOp(map, m => {
      if (m.getLayer(LAYER.DETECTED_HALO))   m.setFilter(LAYER.DETECTED_HALO,   ["==", ["get", "source_id"],    detectedKey]);
      if (m.getLayer(LAYER.RRC_HALO))        m.setFilter(LAYER.RRC_HALO,        ["==", ["get", "id"],           rrcKey]);
      if (m.getLayer(LAYER.OFFSET_SELECTED)) m.setFilter(LAYER.OFFSET_SELECTED, ["==", ["get", "detection_id"], offsetKey]);
    });
  }, [map, selectedWellId, selectedWellType]);

  // ---------------------------------------------------------------------------
  // Filter sync — applies to core + ring only (no_well layer always visible)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;

    const statusFilter = activeFilters.length > 0
      ? ["in", ["coalesce", ["get", "match_status"], "unverified"], ["literal", activeFilters]]
      : ["literal", true];

    // Combine status filter with no_well exclusion
    const combinedFilter = activeFilters.length > 0
      ? ["all", IS_NOT_REJECTED, statusFilter]
      : IS_NOT_REJECTED;

    safeMapOp(map, m => {
      [LAYER.DETECTED_CORE, LAYER.DETECTED_RING].forEach(id => {
        if (m.getLayer(id)) m.setFilter(id, combinedFilter);
      });
    });
  }, [map, activeFilters]);

  // ---------------------------------------------------------------------------
  // Offset line builder — runs on moveend + source load
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;

    const buildLines = debounce(() => {
      if (!map.getSource(DETECTED_SOURCE)) return;
      fetchOffsetPairs(map);
    }, 400);

    const onSourceData = (e) => {
      if (e.sourceId === DETECTED_SOURCE && e.isSourceLoaded) buildLines();
    };

    buildLines();
    map.on("moveend", buildLines);
    map.on("sourcedata", onSourceData);

    return () => {
      map.off("moveend", buildLines);
      map.off("sourcedata", onSourceData);
    };
  }, [map, fetchOffsetPairs]);

  // ---------------------------------------------------------------------------
  // Fly to selected well on selection
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map) return;
    const detail = useWellFinderStore.getState().selectedWell;
    if (!detail) return;

    // RRC side selected → center on the RRC record's own coords; otherwise
    // detected-preferred (unchanged). panelWillOpen is truthy for any matched
    // pair, so the left-shift + zoom floor apply identically for either side.
    const coords = (selectedWellType === "rrc" && detail.rrc)
      ? [detail.rrc.lng, detail.rrc.lat]
      : detail.detected
        ? [detail.detected.lng, detail.detected.lat]
        : detail.rrc
          ? [detail.rrc.lng, detail.rrc.lat]
          : null;

    if (!coords) return;
    focusWell(map, coords, { panelWillOpen: !!detail.link?.rrc_asset_id, duration: 600 });
  }, [map, selectedWellId, selectedWellType]);

  return null;
}
